import { getAirportTimezone } from "./iataTimezones";

// Segment types that have a meaningful "travel duration" (departure → arrival).
// Accommodations/activities/car_rentals express duration as "nights" instead.
const TRANSIT_TYPES = new Set(["flight", "train", "bus", "cruise", "rideshare"]);

/**
 * Format a duration in minutes as a short human-readable string.
 * Examples:
 *   75   -> "1h 15m"
 *   90   -> "1h 30m"
 *   45   -> "45m"
 *   1560 -> "1d 2h"    (>= 24h wraps to days + hours)
 */
export const formatDurationMinutes = (totalMinutes) => {
  if (typeof totalMinutes !== "number" || !isFinite(totalMinutes) || totalMinutes <= 0) {
    return null;
  }
  const mins = Math.round(totalMinutes);
  if (mins >= 24 * 60) {
    const days = Math.floor(mins / (24 * 60));
    const hours = Math.floor((mins % (24 * 60)) / 60);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Convert a local wall-clock date/time in a specific IANA timezone to a UTC Date.
 * Works by:
 *   1. Parse the wall-clock string AS IF it were UTC (this is our "pseudo-UTC").
 *   2. Project that pseudo-UTC into the target timezone and into UTC.
 *   3. The difference between the two projections is the timezone's offset at
 *      that moment — subtract it from the pseudo-UTC to get the true UTC.
 * Handles DST transitions correctly because the offset is computed from the
 * target date/time itself.
 */
const tzWallTimeToUtc = (dateStr, timeStr, tzName) => {
  if (!dateStr || !timeStr || !tzName) return null;
  const iso = `${dateStr}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}Z`;
  const pseudoUtc = new Date(iso);
  if (isNaN(pseudoUtc.getTime())) return null;
  try {
    // en-US sv-SE -> ISO-ish "YYYY-MM-DD HH:MM:SS" ordering; use "en-CA" to get "YYYY-MM-DD, HH:MM:SS" order reliably.
    const asTz = new Date(pseudoUtc.toLocaleString("en-US", { timeZone: tzName }));
    const asUtc = new Date(pseudoUtc.toLocaleString("en-US", { timeZone: "UTC" }));
    const offsetMs = asTz.getTime() - asUtc.getTime();
    if (!isFinite(offsetMs)) return null;
    return new Date(pseudoUtc.getTime() - offsetMs);
  } catch {
    return null;
  }
};

/**
 * Naive same-timezone math: treat wall-clock date+time values as if they were
 * in the same timezone and compute the raw difference.
 */
const naiveDurationMinutes = (startDate, startTime, endDate, endTime) => {
  if (!startDate || !startTime || !endDate || !endTime) return null;
  try {
    const st = new Date(`${startDate}T${startTime.length === 5 ? startTime + ":00" : startTime}`);
    const et = new Date(`${endDate}T${endTime.length === 5 ? endTime + ":00" : endTime}`);
    if (isNaN(st.getTime()) || isNaN(et.getTime())) return null;
    const diffMin = (et.getTime() - st.getTime()) / 60000;
    return diffMin > 0 ? diffMin : null;
  } catch {
    return null;
  }
};

/**
 * Compute the travel duration for a segment.
 * Returns:
 *   { minutes, formatted, tzAware }  on success
 *   null                              when duration can't be computed
 *
 * Logic:
 *   - Non-transit types (accommodation, activity, car_rental) → null (they use nights instead).
 *   - Flights: if both airports have a known IANA timezone, compute via tz-aware math.
 *              Otherwise fall back to naive math (may be misleading across timezones;
 *              caller can choose to suppress by checking tzAware=false).
 *   - Train/bus/cruise/rideshare: naive same-timezone math. This is correct for
 *     point-to-point trips that don't cross timezones (the overwhelming majority).
 */
export const computeSegmentDuration = (segment) => {
  if (!segment) return null;
  const type = (segment.type || "").toLowerCase();
  if (!TRANSIT_TYPES.has(type)) return null;

  const startDate = segment.start_date;
  const endDate = segment.end_date || segment.start_date;
  const startTime = segment.start_time;
  const endTime = segment.end_time;
  if (!startDate || !startTime || !endTime) return null;

  if (type === "flight") {
    const fromTz = getAirportTimezone(segment.airport_from_iata);
    const toTz = getAirportTimezone(segment.airport_to_iata);
    if (fromTz && toTz) {
      const depUtc = tzWallTimeToUtc(startDate, startTime, fromTz);
      const arrUtc = tzWallTimeToUtc(endDate, endTime, toTz);
      if (depUtc && arrUtc) {
        const diffMin = (arrUtc.getTime() - depUtc.getTime()) / 60000;
        if (diffMin > 0) {
          return {
            minutes: diffMin,
            formatted: formatDurationMinutes(diffMin),
            tzAware: true,
          };
        }
      }
    }
    // Fall through to naive — may be wrong across timezones; mark accordingly.
    const naive = naiveDurationMinutes(startDate, startTime, endDate, endTime);
    if (naive !== null) {
      return { minutes: naive, formatted: formatDurationMinutes(naive), tzAware: false };
    }
    return null;
  }

  // Train / bus / cruise / rideshare — naive math.
  const naive = naiveDurationMinutes(startDate, startTime, endDate, endTime);
  if (naive === null) return null;
  return { minutes: naive, formatted: formatDurationMinutes(naive), tzAware: false };
};

/**
 * Aggregate travel totals for a whole trip.
 * Returns counts + total transit minutes per transit type, plus "beds"
 * (number of distinct accommodation segments — a rough proxy for how many
 * different beds the traveler slept in).
 *
 * Cancelled segments are filtered out by the caller; this helper trusts its
 * input. Mistakes DO count (money spent, time taken).
 */
export const buildTripTravelSummary = (segments) => {
  const empty = { count: 0, totalMinutes: 0, hasApproximations: false };
  const summary = {
    flight: { ...empty },
    train: { ...empty },
    bus: { ...empty },
    cruise: { ...empty },
    rideshare: { ...empty },
    // Road trips contribute to "in a car" time but are counted separately
    // from rideshares — the recap reads "9 rides · 3 road trips · NNh in a
    // car". Drive minutes come from the cached Mapbox `road_trip_route`
    // attached to each multi-day car_rental segment by the gadjet-data /
    // road-trip-route endpoint.
    road_trip: { ...empty },
    beds: 0,
    totalTransitMinutes: 0,
  };
  if (!Array.isArray(segments)) return summary;

  for (const seg of segments) {
    const type = (seg?.type || "").toLowerCase();
    if (type === "accommodation") {
      summary.beds += 1;
      continue;
    }
    if (type === "car_rental") {
      // Only count rentals that span >= 2 days as "road trips" — same rule
      // the road-trip block uses, keeps the recap consistent with the UI.
      const route = seg?.road_trip_route;
      const driveSec = route?.total_duration_sec;
      const isMultiDay = seg.start_date && seg.end_date && seg.start_date !== seg.end_date;
      if (isMultiDay && driveSec && driveSec > 0) {
        summary.road_trip.count += 1;
        const mins = Math.round(driveSec / 60);
        summary.road_trip.totalMinutes += mins;
        summary.totalTransitMinutes += mins;
      }
      continue;
    }
    if (!TRANSIT_TYPES.has(type) || !summary[type]) continue;
    summary[type].count += 1;
    const d = computeSegmentDuration(seg);
    if (d && d.minutes > 0) {
      summary[type].totalMinutes += d.minutes;
      summary.totalTransitMinutes += d.minutes;
      if (!d.tzAware && type === "flight") {
        summary[type].hasApproximations = true;
      }
    }
  }
  return summary;
};

/**
 * Check whether a trip has ended — every segment's end date is strictly before
 * today (user's local date inferred from browser). Returns false when the trip
 * has no end date or contains a segment ending today or later.
 */
export const isTripEnded = (trip, segments) => {
  if (!trip) return false;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  // Prefer the latest segment end_date when available — handles trips where
  // segments extend past trip.end_date or vice versa.
  let latest = trip.end_date || null;
  if (Array.isArray(segments)) {
    for (const s of segments) {
      const d = s?.end_date || s?.start_date;
      if (d && (!latest || d > latest)) latest = d;
    }
  }
  if (!latest) return false;
  return latest < todayStr;
};

/**
 * Inclusive day count between two YYYY-MM-DD strings (or Date-parsable). Uses
 * UTC math so DST doesn't flip the count by ±1 at midnight boundaries. Returns
 * null when either side is missing or invalid.
 */
const inclusiveDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const s = new Date(`${startDate}T00:00:00Z`);
  const e = new Date(`${endDate}T00:00:00Z`);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  if (e.getTime() < s.getTime()) return null;
  return Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
};

/**
 * Build a short, sharable recap of a completed trip.
 * Returns `{ text, parts }` where `parts` is an array of strings (useful for
 * styled rendering) and `text` is the joined single-line version. Returns
 * `null` for trips that haven't ended yet or have no recap-worthy content.
 */
export const buildTripRecap = (segments, trip) => {
  if (!isTripEnded(trip, segments)) return null;
  const summary = buildTripTravelSummary(segments || []);
  const parts = [];

  // Lead with total trip duration in days. Prefer trip.start/end_date; fall
  // back to the earliest segment start ↔ latest segment end so trips that
  // were never given explicit dates still get a count.
  let tripStart = trip?.start_date || null;
  let tripEnd = trip?.end_date || null;
  if (Array.isArray(segments)) {
    for (const s of segments) {
      const a = s?.start_date;
      const b = s?.end_date || s?.start_date;
      if (a && (!tripStart || a < tripStart)) tripStart = a;
      if (b && (!tripEnd || b > tripEnd)) tripEnd = b;
    }
  }
  const totalDays = inclusiveDaysBetween(tripStart, tripEnd);
  if (totalDays && totalDays > 0) {
    parts.push(`${totalDays} ${totalDays === 1 ? "day" : "days"}`);
  }

  if (summary.beds > 0) {
    parts.push(`${summary.beds} ${summary.beds === 1 ? "bed" : "beds"}`);
  }

  const modes = [
    { key: "flight", single: "flight", plural: "flights", verb: "in the air" },
    { key: "train", single: "train", plural: "trains", verb: "on rails" },
    { key: "bus", single: "bus", plural: "buses", verb: "on the road" },
    { key: "cruise", single: "cruise", plural: "cruises", verb: "at sea" },
    // "rideshare" is intentionally absent — handled below as part of the
    // shared "in a car" pool that also includes road trips.
  ];

  for (const m of modes) {
    const stat = summary[m.key];
    if (!stat.count) continue;
    const label = `${stat.count} ${stat.count === 1 ? m.single : m.plural}`;
    const timeFmt = formatDurationMinutes(stat.totalMinutes);
    if (timeFmt) {
      const approx = stat.hasApproximations ? "~" : "";
      parts.push(`${label} · ${approx}${timeFmt} ${m.verb}`);
    } else {
      parts.push(label);
    }
  }

  // Combined "in a car" line. Pools rideshare + road-trip drive minutes so
  // the headline reflects total time spent driving/being driven, while
  // showing separate counts ("9 rides · 3 road trips · 27h 13m in a car").
  const ride = summary.rideshare;
  const road = summary.road_trip;
  const carParts = [];
  if (ride.count) {
    carParts.push(`${ride.count} ${ride.count === 1 ? "ride" : "rides"}`);
  }
  if (road.count) {
    carParts.push(`${road.count} ${road.count === 1 ? "road trip" : "road trips"}`);
  }
  if (carParts.length) {
    const totalCarMinutes = ride.totalMinutes + road.totalMinutes;
    const timeFmt = formatDurationMinutes(totalCarMinutes);
    if (timeFmt) {
      parts.push(`${carParts.join(" · ")} · ${timeFmt} in a car`);
    } else {
      parts.push(carParts.join(" · "));
    }
  }

  if (parts.length === 0) return null;
  return { text: parts.join(" · "), parts, summary };
};
